import '../contracts/DevicesFranquias_GetDevicesByFranquiasResponse';
import '../contracts/WhatsappMessageTemplates_GetTemplatesByFranquiasResponse';
import type { DevicesFranquias_InboxDeviceRow } from '../contracts/DevicesFranquias_InboxDeviceRow';
import type { WhatsappMessageTemplates_TemplateRow } from '../contracts/WhatsappMessageTemplates_TemplateRow';
import { ref } from 'vue'
import { httpService } from '../services/httpService'
import { useFiltro } from './useFiltro'
import { log } from '../utils/DebugLogger'
import { config } from '../services/configService'

// Shared state — all consumers see the same devices list
const devices = ref([])
const isLoading = ref(false)
const error = ref<string | null>(null)

/**
 * Base URL for the unified Messaging Service (Go microservice).
 * Handles both Instagram DMs and WhatsApp proxy endpoints.
 */
const MESSAGING_SERVICE_URL = config.api.messagingServiceUrl

/**
 * Derive WebSocket URL from the HTTP URL (http→ws, https→wss).
 */
const MESSAGING_WS_URL = MESSAGING_SERVICE_URL
  ? MESSAGING_SERVICE_URL.replace(/^http/, 'ws') + '/ws/channels/{channelId}'
  : ''

if (!MESSAGING_SERVICE_URL) {
  console.error('[useMessagingInbox] VITE_MESSAGING_SERVICE_HOST is not configured — messaging will not work')
}

/**
 * Custom endpoint routing for Instagram generic-server devices.
 * Maps generic-server endpoint keys to Go microservice routes.
 */
const INSTAGRAM_ENDPOINTS = {
  status:      'GET /status/{channelId}',
  chats:       'GET /chats/{channelId}',
  messages:    'GET /messages/{channelId}/{chatId}?page={page}&pageSize={pageSize}',
  sendText:    'POST /send_text/{channelId}',
  sendMedia:   'POST /send_media/{channelId}',
  sendButtons: 'POST /send_buttons/{channelId}',
  media:       'GET /media/{channelId}/{messageId}',
  deleteMsg:   'DELETE /delete_message/{channelId}/{messageId}',
}

/**
 * Custom endpoint routing for WhatsApp generic-server devices.
 * Maps generic-server endpoint keys to Go microservice WhatsApp proxy routes.
 */
const WHATSAPP_ENDPOINTS = {
  status:      'GET /status/{channelId}',
  chats:       'GET /chats/{channelId}',
  messages:    'GET /messages/{channelId}/{chatId}?page={page}&pageSize={pageSize}',
  sendText:    'POST /send_text/{channelId}',
  sendMedia:   'POST /send_media/{channelId}',
  sendButtons: 'POST /send_buttons/{channelId}',
  media:       'GET /media/{channelId}/{messageId}',
  deleteMsg:   'DELETE /delete_message/{channelId}/{messageId}',
}

/**
 * Maps backend provider_type to the messaging-inbox library's providerType.
 * Both WhatsApp and Instagram now use the unified messaging-service via generic-server.
 */
const mapProviderType = (backendType: any) => {
  switch (backendType) {
    case 'whatsapp': return 'generic-server'
    case 'instagram': return 'generic-server'
    default: return 'generic-server'
  }
}

export function useMessagingInbox() {

  const { selectedFranchises } = useFiltro()

  /**
   * Fetch saved messages (pre-built templates) grouped by franchise.
   * Returns a Map<franquiaId, template[]> for fast per-device lookup.
   */
  const fetchSavedMessagesByFranquias = async (franquiaIds: number[]) => {
    try {
      const response = await httpService.post(
        '/index.php/config/whatsapp_message_templates/get_templates_by_franquias',
        { franquia_ids: franquiaIds },
        { responseContract: 'WhatsappMessageTemplates_GetTemplatesByFranquiasResponse', inputContract: 'WhatsappMessageTemplates_GetTemplatesByFranquiasInput' }
      )

      if (!response.success) {
        log('useMessagingInbox', 'fetchSavedMessagesByFranquias', 'Backend returned error', response.error)
        return new Map()
      }

      // The backend returns a deduplicated flat union of all templates across the
      // requested franquias. We assign the full list to every requested franquiaId
      // so that attachSavedMessages can look up templates per device.franquiaIds.
      const flatTemplates: WhatsappMessageTemplates_TemplateRow[] = response.dados?.templates || []

      const mappedTemplates = flatTemplates.map((t) => ({
        id: String(t.template_id),
        label: t.template_name,
        type: t.template_type || 'text',
        content: t.template_content || '',
        mediaUrl: t.template_media_url || null,
      }))

      const templatesMap = new Map<string, typeof mappedTemplates>()
      for (const franquiaId of franquiaIds) {
        templatesMap.set(String(franquiaId), mappedTemplates)
      }

      log('useMessagingInbox', 'fetchSavedMessagesByFranquias', `Loaded ${flatTemplates.length} templates across ${templatesMap.size} franchises`)
      return templatesMap
    } catch (err) {
      log('useMessagingInbox', 'fetchSavedMessagesByFranquias', 'Error', err, 'error')
      return new Map()
    }
  }

  /**
   * Attach prebuiltMessages to each device by matching device.franquiaIds
   * with the templates-per-franchise map. Deduplicates by template id.
   * Only attaches to WhatsApp devices — Instagram doesn't use templates.
   */
  const attachSavedMessages = (deviceList: any, templatesMap: any) => {
    for (const device of deviceList) {
      // Only WhatsApp devices use prebuilt messages; Instagram does not
      if (device.channel !== 'whatsapp') {
        device.prebuiltMessages = []
        continue
      }

      const seen = new Set()
      const messages = []

      for (const fId of device.franquiaIds) {
        const franquiaTemplates = templatesMap.get(String(fId)) || []
        for (const tpl of franquiaTemplates) {
          if (seen.has(tpl.id)) continue
          seen.add(tpl.id)
          messages.push(tpl)
        }
      }

      device.prebuiltMessages = messages
    }
  }

  const fetchInboxDevices = async () => {
    const franquiaIds = selectedFranchises.value.map((f: any) => f.id)

    if (franquiaIds.length === 0) {
      devices.value = []
      return
    }

    isLoading.value = true
    error.value = null

    try {
      // Fetch devices and saved messages in parallel
      const [devicesResponse, templatesMap] = await Promise.all([
        httpService.post(
          '/index.php/config/devices_franquias/get_devices_by_franquias',
          { franquia_ids: franquiaIds },
          { responseContract: 'DevicesFranquias_GetDevicesByFranquiasResponse', inputContract: 'DevicesFranquias_GetDevicesByFranquiasInput' }
        ),
        fetchSavedMessagesByFranquias(franquiaIds)
      ])

      if (!devicesResponse.success) {
        error.value = devicesResponse.error || 'Erro ao buscar dispositivos'
        devices.value = []
        return
      }

      // Flat array of typed InboxDeviceRow — each device has its own api_url and franquia_ids
      const rawDevices: DevicesFranquias_InboxDeviceRow[] = devicesResponse.dados || []

      const result: any = []
      for (const device of rawDevices) {
        if (!device.instance_token || !MESSAGING_SERVICE_URL) continue

        const providerType = mapProviderType(device.provider_type)
        const isInstagram = device.provider_type === 'instagram'
        const isWhatsApp = device.provider_type === 'whatsapp'

        result.push({
          id: String(device.device_id),
          label: device.label,
          apiUrl: MESSAGING_SERVICE_URL,
          instanceToken: device.instance_token,
          instanceName: device.instance_name,
          providerType,
          channel: device.provider_type,
          ...(device.capabilities ? { capabilities: device.capabilities } : {}),
          ...(isInstagram ? { endpoints: INSTAGRAM_ENDPOINTS } : {}),
          ...(isWhatsApp ? { endpoints: WHATSAPP_ENDPOINTS } : {}),
          websocket: { enabled: true, url: MESSAGING_WS_URL },
          franquiaIds: (device.franquia_ids || []).map(String),
        })
      }

      // Attach saved messages to each device based on franchise overlap
      attachSavedMessages(result, templatesMap)

      devices.value = result
      log('useMessagingInbox', 'fetchInboxDevices', `Loaded ${result.length} devices with saved messages`)
    } catch (err) {
      error.value = err.message || 'Erro ao buscar dispositivos'
      devices.value = []
      log('useMessagingInbox', 'fetchInboxDevices', 'Error', err, 'error')
    } finally {
      isLoading.value = false
    }
  }

  return { devices, isLoading, error, fetchInboxDevices }
}
